import { useCallback, useState } from 'react'
import { ActionIcon, Box, Checkbox, Flex } from '@mantine/core'
import { useForm, zodResolver } from '@mantine/form'
import {
  IconArrowDown,
  IconArrowUp,
  IconEdit,
  IconTrash,
} from '@tabler/icons-react'
import dayjs from 'dayjs'
import { z } from 'zod'

import { Button } from '@/components/Button/Button'
import { ConfirmationModal } from '@/components/ConfirmationModal/ConfirmationModal'
import { NumberInput } from '@/components/NumberInput/NumberInput'
import { SeparateDateInput } from '@/components/SeparateDateInput/SeparateDateInput'
import { Text } from '@/components/Text/Text'
import { TextInput } from '@/components/TextInput/TextInput'
import { Title } from '@/components/Title/Title'

import type { GoalStepData } from '@/__generated__/requests'

import {
  useDeleteGoalStep,
  useReorderGoalSteps,
  useToggleGoalStep,
  useUpdateGoalStep,
} from '@/hooks/useGoalMutations.hook'

type GoalStepsListProps = {
  goalId: number
  isActive: boolean
  onStepsChange: (steps: GoalStepData[]) => void
  steps: GoalStepData[]
}

const stepSchema = z.object({
  targetAmount: z.number().optional(),
  targetDate: z.string().optional(),
  title: z.string().min(1, 'Title is required'),
})

type StepFormValues = z.infer<typeof stepSchema>

export function GoalStepsList({
  goalId,
  isActive,
  onStepsChange,
  steps,
}: GoalStepsListProps) {
  const [editingStepId, setEditingStepId] = useState<number | null>(null)
  const [deletingStepId, setDeletingStepId] = useState<number | null>(null)

  const editForm = useForm<StepFormValues>({
    initialValues: {
      targetAmount: undefined,
      targetDate: '',
      title: '',
    },
    validate: zodResolver(stepSchema),
  })

  const updateStepMutation = useUpdateGoalStep(() => {
    setEditingStepId(null)
    editForm.reset()
  })

  const deleteStepMutation = useDeleteGoalStep()
  const toggleStepMutation = useToggleGoalStep()
  const reorderStepsMutation = useReorderGoalSteps()

  const handleUpdateStep = (stepId: number, values: StepFormValues) => {
    updateStepMutation.mutate(
      {
        goalId,
        requestBody: {
          targetAmount: values.targetAmount || undefined,
          targetDate: values.targetDate || undefined,
          title: values.title,
        },
        stepId,
      },
      {
        onSuccess: (data: GoalStepData) => {
          onStepsChange(steps.map(s => (s.id === stepId ? data : s)))
        },
      },
    )
  }

  const handleDeleteStep = () => {
    if (!deletingStepId) return

    deleteStepMutation.mutate(
      { goalId, stepId: deletingStepId },
      {
        onSuccess: () => {
          onStepsChange(steps.filter(s => s.id !== deletingStepId))
          setDeletingStepId(null)
        },
      },
    )
  }

  const handleToggleStep = (stepId: number) => {
    toggleStepMutation.mutate(
      { goalId, stepId },
      {
        onSuccess: (data: GoalStepData) => {
          onStepsChange(steps.map(s => (s.id === stepId ? data : s)))
        },
      },
    )
  }

  const handleMoveStep = useCallback(
    (stepId: number, direction: 'up' | 'down') => {
      const currentIndex = steps.findIndex(s => s.id === stepId)

      if (currentIndex < 0) return

      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1

      if (newIndex < 0 || newIndex >= steps.length) return

      const newSteps = [...steps]
      const [moved] = newSteps.splice(currentIndex, 1)

      newSteps.splice(newIndex, 0, moved)

      onStepsChange(newSteps)

      reorderStepsMutation.mutate({
        goalId,
        requestBody: { stepIds: newSteps.map(s => s.id) },
      })
    },
    [steps, goalId, reorderStepsMutation, onStepsChange],
  )

  const startEditing = (step: GoalStepData) => {
    setEditingStepId(step.id)
    editForm.setValues({
      targetAmount: step.targetAmount ?? undefined,
      targetDate: step.targetDate ?? '',
      title: step.title,
    })
  }

  return (
    <Box
      p={24}
      style={{
        backgroundColor: 'var(--mantine-other-colors-white)',
        borderRadius: 8,
        boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.04)',
      }}
    >
      <Title mb={16} variant="h2_small">
        Steps
      </Title>

      {steps.length === 0 && (
        <Text variant="body_2_medium_black">No steps yet</Text>
      )}

      <Flex direction="column" gap={8}>
        {steps.map((step, index) => (
          <Box
            key={step.id}
            p={12}
            style={{
              backgroundColor: step.completed
                ? 'var(--mantine-other-colors-grey-bg, #f8f9fa)'
                : 'transparent',
              border: '1px solid var(--mantine-other-colors-light-grey)',
              borderRadius: 8,
            }}
          >
            {editingStepId === step.id ? (
              <Box
                component="form"
                maw={400}
                noValidate
                onSubmit={editForm.onSubmit(values =>
                  handleUpdateStep(step.id, values),
                )}
              >
                <TextInput
                  label="Step title"
                  placeholder="Enter step title"
                  {...editForm.getInputProps('title')}
                />
                <SeparateDateInput
                  label="Target date"
                  onChange={value => {
                    if (value instanceof Date) {
                      editForm.setFieldValue(
                        'targetDate',
                        dayjs(value).format('YYYY-MM-DD'),
                      )
                    } else {
                      editForm.setFieldValue('targetDate', '')
                    }
                  }}
                  value={
                    editForm.values.targetDate
                      ? dayjs(editForm.values.targetDate).toDate()
                      : undefined
                  }
                />
                <NumberInput
                  hideControls
                  label="Target amount"
                  placeholder="Enter amount"
                  {...editForm.getInputProps('targetAmount')}
                />
                <Flex gap={8} mt={16}>
                  <Button
                    loading={updateStepMutation.isPending}
                    type="submit"
                    variant="primary"
                  >
                    Save
                  </Button>
                  <Button
                    onClick={() => {
                      setEditingStepId(null)
                      editForm.reset()
                    }}
                    variant="outline"
                  >
                    Cancel
                  </Button>
                </Flex>
              </Box>
            ) : (
              <Flex align="center" justify="space-between">
                <Flex align="center" gap={8}>
                  {isActive && (
                    <Checkbox
                      checked={step.completed}
                      onChange={() => handleToggleStep(step.id)}
                    />
                  )}
                  <Box>
                    <Text
                      style={{
                        textDecoration: step.completed
                          ? 'line-through'
                          : 'none',
                      }}
                      variant="body_2_semibold_black"
                    >
                      {step.title}
                    </Text>
                    <Flex gap={12}>
                      {step.targetDate && (
                        <Text variant="body_2_medium_black">
                          Due: {dayjs(step.targetDate).format('MMM D, YYYY')}
                        </Text>
                      )}
                      {step.targetAmount !== undefined &&
                        step.targetAmount !== null && (
                          <Text variant="body_2_medium_black">
                            Target: ${step.targetAmount}
                          </Text>
                        )}
                    </Flex>
                  </Box>
                </Flex>

                {isActive && (
                  <Flex gap={4}>
                    <ActionIcon
                      disabled={index === 0}
                      onClick={() => handleMoveStep(step.id, 'up')}
                      size="sm"
                      variant="subtle"
                    >
                      <IconArrowUp size={14} />
                    </ActionIcon>
                    <ActionIcon
                      disabled={index === steps.length - 1}
                      onClick={() => handleMoveStep(step.id, 'down')}
                      size="sm"
                      variant="subtle"
                    >
                      <IconArrowDown size={14} />
                    </ActionIcon>
                    <ActionIcon
                      onClick={() => startEditing(step)}
                      size="sm"
                      variant="subtle"
                    >
                      <IconEdit size={14} />
                    </ActionIcon>
                    <ActionIcon
                      color="red"
                      onClick={() => setDeletingStepId(step.id)}
                      size="sm"
                      variant="subtle"
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Flex>
                )}
              </Flex>
            )}
          </Box>
        ))}
      </Flex>

      <ConfirmationModal
        button={{ label: 'Delete', variant: 'error' }}
        centered
        description="Are you sure you want to delete this step? This action cannot be undone."
        isLoading={deleteStepMutation.isPending}
        onClose={() => setDeletingStepId(null)}
        onConfirm={handleDeleteStep}
        opened={deletingStepId !== null}
        title="Delete Step"
      />
    </Box>
  )
}
